import { dayAxis } from '@/components/charts/generic/dayAxis';
import { depthAxisRange } from '@/components/charts/generic/depthAxisRange';
import { hourAxis } from '@/components/charts/generic/hourAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { OptoColors } from '@/config/settings';
import type { DepthPoint, DepthResolution } from '@/domain/depthSeries';
import { formatUniversalUnitByLocale, type Locale } from '@/utils/localeFormatters';

/** Palette hex. A token name such as `chart-axis` is not a colour Chart.js can paint, so it draws black. */
const HOURLY_COLOR = OptoColors.OceanGreen;
const DAILY_COLOR = OptoColors.Orange;

export interface BioscopeDepthChartLabels {
    hourly: string;
    daily: string;
}

export interface BuildBioscopeDepthChartParams {
    resolution: DepthResolution;
    hourly: DepthPoint[];
    daily: DepthPoint[];
    locale: Locale;
    labels: BioscopeDepthChartLabels;
}

/**
 * Hourly draws both series on an hour axis, the daily average held flat across each day.
 * Daily draws only the daily line on a day axis.
 */
export function buildBioscopeDepthChart(
    params: BuildBioscopeDepthChartParams
): MetricChartSpec<'line'> {
    const { resolution, hourly, daily, locale, labels } = params;
    const dailyByDay = new Map(daily.map((point) => [point.time.slice(0, 10), point.value]));

    if (resolution === 'day') {
        const days = dayAxis(daily, (point) => point.time.slice(0, 10));
        return spec(
            days,
            'day',
            [
                {
                    key: 'daily',
                    label: labels.daily,
                    color: DAILY_COLOR,
                    tension: 0,
                    data: days.map((day) => ({ day, value: dailyByDay.get(day) ?? null })),
                },
            ],
            locale
        );
    }

    const hours = hourAxis(hourly, (point) => point.time);
    const hourlyByHour = new Map(hourly.map((point) => [point.time, point.value]));
    return spec(
        hours,
        'hour',
        [
            {
                key: 'hourly',
                label: labels.hourly,
                color: HOURLY_COLOR,
                tension: 0,
                data: hours.map((hour) => ({ day: hour, value: hourlyByHour.get(hour) ?? null })),
            },
            {
                key: 'daily',
                label: labels.daily,
                color: DAILY_COLOR,
                stepped: true,
                data: hours.map((hour) => ({
                    day: hour,
                    value: dailyByDay.get(hour.slice(0, 10)) ?? null,
                })),
            },
        ],
        locale
    );
}

function spec(
    keys: string[],
    xAxisKind: 'hour' | 'day',
    series: MetricChartSpec<'line'>['series'],
    locale: Locale
): MetricChartSpec<'line'> {
    const values = series.flatMap((item) =>
        item.data.flatMap((point) => (point.value == null ? [] : [point.value]))
    );
    const bounds = depthAxisRange(values);
    return {
        kind: 'line',
        days: keys,
        xAxisKind,
        series: series.map((item) => ({ ...item, showInLegend: true })),
        left: {
            label: '',
            unit: 'm',
            reverse: true,
            beginAtZero: false,
            ...(bounds ? { min: bounds.min, max: bounds.max } : {}),
            format: (value) => formatUniversalUnitByLocale(value, 'm', locale, 1),
        },
        locale,
    };
}
